import React, { useCallback } from "react";
import { Box, Flex, Button, Tooltip } from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";

export interface Step {
  name: string;
  description: string;
  isCompleted: boolean;
  key: string;
  depends?: string[];
}

export interface StepperProps {
  steps: Step[];
  currentStep: number;
  onClick: (step: number) => void;
}

export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStep,
  onClick,
}) => {
  const calculateDepends = useCallback(
    (step: Step) => {
      if (!step.depends) return true;
      return step.depends.every(
        (dep) => steps.find((s) => s.key === dep)?.isCompleted
      );
    },
    [steps]
  );

  return (
    <Flex
      flexDir={"column"}
      flex={1}
      gap={4}
      align="stretch"
      transition={"all 0.5s ease"}
    >
      <Flex align="center">
        {steps.map((step, index) => {
          return (
            <React.Fragment key={step.name}>
              <Tooltip
                label={step.description}
                aria-label={step.description}
                placement="top"
              >
                <Flex flexDir={"column"} alignItems="center">
                  <Button
                    size="sm"
                    width={8}
                    height={8}
                    variant={step.isCompleted ? "solid" : "outline"}
                    colorScheme={step.isCompleted ? "brand" : "gray"}
                    disabled={!calculateDepends(step)}
                    onClick={() => onClick(index)}
                    borderRadius="full"
                    color={
                      index === currentStep && !step.isCompleted
                        ? "brand.500"
                        : undefined
                    }
                    border={index === currentStep ? "2px solid" : "1px solid"}
                    borderColor={
                      index === currentStep
                        ? step.isCompleted
                          ? "blue.500"
                          : "brand.500"
                        : "inherit"
                    }
                    transition="all 0.5s ease"
                  >
                    {step.isCompleted ? (
                      <CheckIcon width={3} height={3} />
                    ) : (
                      index + 1
                    )}
                  </Button>
                  <Box
                    bottom={2}
                    position={"absolute"}
                    fontSize="sm"
                    fontWeight={step.isCompleted ? "bold" : "medium"}
                    color={index === currentStep ? "brand.500" : "gray.500"}
                    transition="all 0.5s ease"
                  >
                    {step.name}
                  </Box>
                </Flex>
              </Tooltip>
              {index < steps.length - 1 && (
                <Box
                  flex={1}
                  h="2px"
                  bg={steps[index].isCompleted ? "brand.500" : "gray.200"}
                  mx={2}
                />
              )}
            </React.Fragment>
          );
        })}
      </Flex>
    </Flex>
  );
};
